import {
  HERO_BACKGROUND_IMAGE_QUALITY,
  HERO_BACKGROUND_IMAGE_SRCSET_WIDTHS,
} from "./hero-image-constants";
import { urlFor } from "./sanity";

/** Matches @sanity/image-url `urlFor` input */
export type SanityImageSource = Parameters<typeof urlFor>[0];

const DEFAULT_QUALITY = 78;

function optimizedBase(source: SanityImageSource) {
  return urlFor(source).auto("format").quality(DEFAULT_QUALITY);
}

function optimizedHeroBase(source: SanityImageSource) {
  return urlFor(source).auto("format").quality(HERO_BACKGROUND_IMAGE_QUALITY);
}

function buildSrcSet(
  source: SanityImageSource,
  widths: readonly number[],
): string {
  return widths
    .map((w) => `${optimizedBase(source).width(w).url()} ${w}w`)
    .join(", ");
}

/** Full-bleed hero / carousel backgrounds */
export function buildHeroBackgroundImageProps(source: SanityImageSource): {
  src: string;
  srcSet: string;
  sizes: string;
} {
  return {
    src: optimizedHeroBase(source).width(1920).url(),
    srcSet: HERO_BACKGROUND_IMAGE_SRCSET_WIDTHS.map(
      (w) => `${optimizedHeroBase(source).width(w).url()} ${w}w`,
    ).join(", "),
    sizes: "100vw",
  };
}

/** Home news grid: up to 4 columns */
export function buildNewsCardImageProps(source: SanityImageSource): {
  src: string;
  srcSet: string;
  sizes: string;
  width: number;
  height: number;
} {
  const widths = [400, 600, 800] as const;
  return {
    src: optimizedBase(source).width(800).height(450).url(),
    srcSet: widths
      .map(
        (w) =>
          `${optimizedBase(source).width(w).height(Math.round((w * 450) / 800)).url()} ${w}w`,
      )
      .join(", "),
    sizes: "(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 25vw",
    width: 800,
    height: 450,
  };
}

/** Article body featured image (width-only URLs preserve source aspect) */
export function buildArticleImageProps(
  source: SanityImageSource,
  variant: "portrait" | "landscape",
): {
  src: string;
  srcSet: string;
  sizes: string;
  width: number;
  height: number;
} {
  if (variant === "portrait") {
    const widths = [400, 600, 900] as const;
    return {
      src: optimizedBase(source).width(600).url(),
      srcSet: buildSrcSet(source, widths),
      sizes: "(max-width: 768px) 100vw, 33vw",
      width: 600,
      height: 450,
    };
  }
  const widths = [800, 1200, 1600] as const;
  return {
    src: optimizedBase(source).width(1200).url(),
    srcSet: buildSrcSet(source, widths),
    sizes: "(max-width: 768px) 100vw, min(100vw, 896px)",
    width: 1200,
    height: 800,
  };
}

/** Inline images in portable text (prose max width) */
export function buildPortableTextImageProps(
  source: SanityImageSource,
  maxWidth = 800,
): {
  src: string;
  srcSet: string;
  sizes: string;
  width: number;
  height: number;
} {
  const widths = [400, 600, maxWidth].filter(
    (w, i, arr) => arr.indexOf(w) === i,
  );
  return {
    src: optimizedBase(source).width(maxWidth).url(),
    srcSet: buildSrcSet(source, widths),
    sizes: `(max-width: 768px) 100vw, ${maxWidth}px`,
    width: maxWidth,
    height: Math.round(maxWidth * 0.5625),
  };
}

/** Gallery grid thumbnails: square crop, up to 4 columns */
export function buildGalleryThumbnailProps(source: SanityImageSource): {
  src: string;
  srcSet: string;
  sizes: string;
  width: number;
  height: number;
} {
  const widths = [300, 400, 600] as const;
  return {
    src: optimizedBase(source).width(400).height(400).url(),
    srcSet: widths
      .map(
        (w) => `${optimizedBase(source).width(w).height(w).url()} ${w}w`,
      )
      .join(", "),
    sizes: "(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw",
    width: 400,
    height: 400,
  };
}

/** Gallery lightbox: large width-only URL preserves source aspect */
export function buildGalleryLightboxProps(source: SanityImageSource): {
  src: string;
  srcSet: string;
  sizes: string;
  width: number;
} {
  const widths = [800, 1200, 1600] as const;
  return {
    src: optimizedBase(source).width(1200).url(),
    srcSet: buildSrcSet(source, widths),
    sizes: "min(100vw, 1200px)",
    width: 1200,
  };
}
